/**
 * DiscrepancyDetectionPage — `/franchise/discrepancies`.
 *
 * Lists franchise discrepancies persisted via the operations slice
 * (`/v1/franchise/ops/discrepancies`). Each row carries severity / status /
 * SLA timer / assignee. Operators can file a new discrepancy from a
 * disputed snapshot (or any free-form description) and update the status.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createDiscrepancy,
  listDiscrepancies,
  listFranchises,
  updateDiscrepancyStatus,
  type DiscrepancySeverity,
  type DiscrepancyStatus,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const STATUSES: readonly DiscrepancyStatus[] = ['OPEN', 'INVESTIGATING', 'RESOLVED', 'CLOSED'];
const SEVERITIES: readonly DiscrepancySeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function DiscrepancyDetectionPage(): JSX.Element {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    franchiseId: '',
    description: '',
    severity: 'MEDIUM' as DiscrepancySeverity,
    expectedAmount: '0',
    actualAmount: '0',
    slaHours: 48,
  });

  const list = useQuery({
    queryKey: ['franchise', 'discrepancy', 'list', statusFilter],
    queryFn: () =>
      listDiscrepancies({
        ...(statusFilter ? { status: statusFilter } : {}),
        pageSize: 100,
      }),
    retry: false,
  });

  const franchises = useQuery({
    queryKey: ['franchise', 'discrepancy', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => {
      if (createForm.franchiseId.length === 0 || createForm.description.length < 3) {
        throw new Error('Franchise + description (min 3 chars) are required.');
      }
      return createDiscrepancy({
        franchiseId: createForm.franchiseId,
        description: createForm.description,
        severity: createForm.severity,
        expectedAmount: createForm.expectedAmount,
        actualAmount: createForm.actualAmount,
        slaHours: createForm.slaHours,
      });
    },
    onSuccess: () => {
      setShowCreate(false);
      setCreateForm({
        franchiseId: '',
        description: '',
        severity: 'MEDIUM',
        expectedAmount: '0',
        actualAmount: '0',
        slaHours: 48,
      });
      void qc.invalidateQueries({ queryKey: ['franchise', 'discrepancy', 'list'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const updateStatus = useMutation({
    mutationFn: (input: { id: string; status: DiscrepancyStatus }) =>
      updateDiscrepancyStatus(input.id, { status: input.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'discrepancy', 'list'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied =
    list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Discrepancy detection"
        description="Discrepancy engine — severity, SLA, assignee, resolution."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showCreate ? 'Cancel' : 'New discrepancy'}
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Total" value={String(list.data?.total ?? 0)} />
        <KPICard
          title="Open"
          value={String(list.data?.openCount ?? 0)}
          hint="Awaiting triage"
        />
        <KPICard
          title="Investigating"
          value={String(list.data?.investigatingCount ?? 0)}
          hint="Assignee working it"
        />
        <KPICard
          title="Resolved"
          value={String(list.data?.resolvedCount ?? 0)}
          hint="Closed within SLA"
        />
      </div>

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Status</span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DiscrepancyStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
        >
          <option value="">All</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {showCreate ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">File a new discrepancy</p>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
            <select
              value={createForm.franchiseId}
              onChange={(e) => setCreateForm({ ...createForm, franchiseId: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Description</label>
            <textarea
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Severity</label>
              <select
                value={createForm.severity}
                onChange={(e) =>
                  setCreateForm({ ...createForm, severity: e.target.value as DiscrepancySeverity })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Expected (NGN)</label>
              <input
                type="text"
                value={createForm.expectedAmount}
                onChange={(e) => setCreateForm({ ...createForm, expectedAmount: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Actual (NGN)</label>
              <input
                type="text"
                value={createForm.actualAmount}
                onChange={(e) => setCreateForm({ ...createForm, actualAmount: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">SLA (hours)</label>
              <input
                type="number"
                min="1"
                max="720"
                value={createForm.slaHours}
                onChange={(e) =>
                  setCreateForm({ ...createForm, slaHours: Number.parseInt(e.target.value, 10) || 48 })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              create.mutate();
            }}
            disabled={create.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {create.isPending ? 'Filing…' : 'File discrepancy'}
          </button>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {list.isPending ? (
        <p className="text-sm text-muted">Loading discrepancies…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseDiscrepancy/read</code> required.
        </DeferredNotice>
      ) : list.isError ? (
        <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No discrepancies recorded.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Severity</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Variance</th>
                <th className="text-left px-4 py-2">Detected</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs">
                    <span className="block font-medium text-gray-900 line-clamp-2">
                      {d.description}
                    </span>
                    <span className="font-mono text-[10px] text-muted">{d.franchiseId.slice(0, 8)}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">{d.kind}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={d.severity} />
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(d.variance)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(d.detectedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {d.status === 'OPEN' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          updateStatus.mutate({ id: d.id, status: 'INVESTIGATING' });
                        }}
                        disabled={updateStatus.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Investigate →
                      </button>
                    ) : d.status === 'INVESTIGATING' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          updateStatus.mutate({ id: d.id, status: 'RESOLVED' });
                        }}
                        disabled={updateStatus.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Resolve →
                      </button>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
